import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { BuyNowStatus } from '@/types/item/ItemSummary';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { useAppSelector } from '@/redux/hooks';
import ItemCardBadge from '@/components/GridItemCard/Card/components/ItemCardBadge/ItemCardBadge';
import ItemCardImage from '@/components/GridItemCard/Card/components/ItemCardImage/ItemCardImage';
import ItemCardMenu from '@/components/ItemCard/components/Menu/ItemCardMenu';
import ItemCardPriceRow from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRow';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import ms from 'ms';
import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';

const UpcomingSalesPriceResultsCard: ItemCardComponent = ({ className, dimension, itemId }) => {
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const { buyNowStatus, catalogStatus, isLocked, isPassed, isSold, saleStartTs } = useAppSelector((state) =>
        getItemSummary(state, itemId)
    );
    const [hover, setHover] = useState(false);
    const [linkHover, setLinkHover] = useState(false);
    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = catalogStatus === 'online' && !biddingWithin7Days;

    const isBuyNow = buyNowStatus === BuyNowStatus.Available;

    const enterHoverState = () => {
        setHover(true);
    };

    const leaveHoverState = () => {
        setHover(false);
    };

    const isPending = isLocked && !isPassed && !isSold && !isLotOpen;
    const auctionDateInfo = isBuyNow ? (
        <BodySecondary color="secondary">
            <FormattedMessage id="buy_now" />
        </BodySecondary>
    ) : (
        <ItemCardShortDate
            allowBidding
            itemId={itemId}
            showDateWithoutYear={showDateWithoutYear}
            showOnlyYear={isSold || isPassed || isPending}
        />
    );
    return (
        <StyledCard
            data-testid={`upcoming-sales-price-results-card-${itemId}`}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <StyledExpansionContainer data-testid="expansion-container">
                <UpcomingSalesPriceResultsCardGrid
                    $dimension={dimension}
                    className={className}
                    data-testid="upcoming-sales-price-results-card-grid"
                >
                    <StyledImageContainer
                        data-testid="image-container"
                        onMouseEnter={() => {
                            setLinkHover(true);
                        }}
                        onMouseLeave={() => {
                            setLinkHover(false);
                        }}
                    >
                        <ItemCardBadge
                            className="left-xs top-xs"
                            itemId={itemId}
                            savedItemsCard
                        />
                        <StyledFavoriteButton>
                            <ItemCardSaveIcon itemId={itemId} />
                        </StyledFavoriteButton>
                        <PlacedItemCardImage
                            dimension={dimension}
                            itemId={itemId}
                            showBidStatus
                        />
                    </StyledImageContainer>
                    <PlacedDetailsForNewMobileGrid>
                        <PlacedItemInfo>
                            <StyledDateInfo>{auctionDateInfo}</StyledDateInfo>
                        </PlacedItemInfo>
                        <PlacedItemCardTitle
                            isHovered={linkHover}
                            itemId={itemId}
                            onMouseEnter={() => {
                                setLinkHover(true);
                            }}
                            onMouseLeave={() => {
                                setLinkHover(false);
                            }}
                            testid="itemCardTitle"
                        />
                        <PlacedPriceInformation>
                            <StyledBidInfo>
                                <ItemCardPriceRow
                                    isHovered={linkHover}
                                    itemId={itemId}
                                    onMouseEnter={() => {
                                        setLinkHover(true);
                                    }}
                                    onMouseLeave={() => {
                                        setLinkHover(false);
                                    }}
                                />
                            </StyledBidInfo>
                            {isLoggedIn && (
                                <ItemCardMenu
                                    itemId={itemId}
                                    showIcon={hover}
                                />
                            )}
                        </PlacedPriceInformation>
                    </PlacedDetailsForNewMobileGrid>
                </UpcomingSalesPriceResultsCardGrid>
            </StyledExpansionContainer>
        </StyledCard>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 181, mobile: 164 },
    ItemCardComponent: UpcomingSalesPriceResultsCard,
};

export default info;

const StyledCard = styled.article`
    position: relative;
`;

const StyledExpansionContainer = styled.section`
    margin: 0 auto;
`;

const PlacedItemCardImage = styled(ItemCardImage)`
    grid-area: itemimage;
`;

const PlacedDetailsForNewMobileGrid = styled.div`
    display: grid;
    overflow: hidden;
    grid-area: alldetailsformobilelayout;
    grid-template-areas: '.' 'iteminfo' 'title' '.' 'currentbidprice';
    grid-template-rows: 6px 22px min-content 4px;

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-rows: 24px min-content 4px;
        grid-template-areas: 'iteminfo' 'title' '.' 'currentbidprice';
    }
`;

const StyledFavoriteButton = styled.span`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
`;

const PlacedItemInfo = styled.div`
    grid-area: iteminfo;
    display: flex;
    justify-content: space-between;
`;

const StyledDateInfo = styled.span`
    width: 100%;
    line-height: 18px;
`;

const PlacedItemCardTitle = styled(ItemCardTitle)`
    grid-area: title;
`;

const StyledBidInfo = styled.div`
    display: flex;
    align-items: center;
`;

const PlacedPriceInformation = styled.div`
    grid-area: currentbidprice;
    display: flex;
    justify-content: space-between;
`;

const StyledImageContainer = styled.div`
    position: relative;
`;

const UpcomingSalesPriceResultsCardGrid = styled.section<{
    $dimension: number;
}>`
    display: grid;
    grid-template-columns: ${({ $dimension }) => $dimension}px;
    grid-template-rows: ${({ $dimension }) => $dimension}px;
    grid-template-areas:
        'itemimage'
        '.'
        'alldetailsformobilelayout';
    justify-content: center;
`;
